import { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import ActionBar from 'raydiant-elements/core/ActionBar';
import Button from 'raydiant-elements/core/Button';
import Paper from 'raydiant-elements/core/Paper';
import FileDropper from 'raydiant-elements/core/FileDropper';
import PlaylistCard from 'raydiant-elements/playlist/PlaylistCard';
import Scrollable from 'raydiant-elements/layout/Scrollable';
import Spacer from 'raydiant-elements/layout/Spacer';
import ApplicationCard from '../../components/ApplicationCard';
import Page from '../../components/Page';
import Grid from '../../components/Grid';
import GridItem from '../../components/Grid/GridItem';
import MarketplaceBanner from '../../components/MarketplaceBanner';
import config from '../../config';
import * as paths from '../../routes/paths';
import {
  createPresentationFromAppVersion,
  createDefaultPlaylist,
  isResourceDeleted,
  getDefaultThemeForUser,
} from '../../utilities';
import { createNewId } from '../../utilities/identifiers';
import * as applicationsPageActions from './actions';
import * as addContentPageActions from '../AddContentPage/actions';
import * as presentationPageActions from '../PresentationPage/actions';
import * as presActions from '../../actions/presentations';
import getMimeTypesForAppVersion from './getMimeTypesForAppVersion';
import useStyles from './ApplicationsPage.styles';
import * as P from '../../clients/mira/types/Presentation';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import selectApplicationsPageData from './selectors';
import { selectDomainForCurrentUser } from '../../selectors/v2/domains';
import { RootState } from '../../reducers';

export const defaultPlaylist = createDefaultPlaylist({
  name: 'Create a Playlist',
});

// Trigger the file upload dialog when creating pictures or videos.
const quickUploadAppIds = [config.picturesAppId, config.videosAppId];

// Allow pictures, videos and presenter to handle drag and drop file uploads.
const fileUploadAppIds = [
  config.picturesAppId,
  config.videosAppId,
  config.presenterAppId,
];

export interface ApplicationsPageProps {
  isModal?: boolean;
}

const ApplicationsPage = ({ isModal }: ApplicationsPageProps) => {
  const location = useLocation();
  const history = useHistory();
  const match = useRouteMatch();
  const classes = useStyles();
  const dispatch = useDispatch();

  const fileInput = useRef<HTMLInputElement>(null);

  const {
    applications,
    currentUser,
    themesById,
    backTo,
    selectionId,
    folderId,
    isLoading,
    createPlaylistTo,
  } = useSelector((state: RootState) =>
    selectApplicationsPageData(state, { location, history, match }),
  );
  const domain = useSelector(selectDomainForCurrentUser);

  useEffect(() => {
    dispatch(applicationsPageActions.loadApplicationsPage());
  }, [dispatch]);

  const getBackToLabel = () => 'Back to Library';

  const goBack = () => history.push(backTo);

  const newPresentation = (applicationId: string) => {
    dispatch(presentationPageActions.clearUnsavedPresentation(applicationId));

    const previewValues = applications
      .map((appl) => appl.currentAppVersion)
      .find((appVersion) => appVersion.applicationId === applicationId);

    history.push(
      paths.newPresentation({
        applicationId,
        selectionId,
        folderId,
        backToLabel: getBackToLabel(),
        // We don't want to come back to the app selector page,
        // forward the backTo to the next page.
        backTo,
        // On presentation save, we want to navigate to the page
        // the applications page was launched from.
        //  ie. /library, /screens, /screens/:id/default, etc...
        saveTo: backTo,
        previewResolution: previewValues?.previewResolution,
        previewOrientation: previewValues?.previewOrientation,
      }),
    );
  };

  const handleFileInputChange = () => {
    if (fileInput.current && fileInput.current.files) {
      createPresentationsForFiles(fileInput.current.files);
    }
  };

  const openFileBrowser = (mimetypes: string[]) => {
    if (fileInput.current) {
      fileInput.current.accept = mimetypes.join(',');
      fileInput.current.click();
    }
  };

  const createPresentationsForFiles = async (fileList: FileList) => {
    if (!currentUser) return;

    const files = Array.from(fileList);
    const fileUploadApps = fileUploadAppIds.map((appId) =>
      applications.find((app) => app.id === appId),
    );

    const presentationsToSave: Array<Promise<P.Presentation>> = [];

    files.forEach((file) => {
      const fileUploadApp = fileUploadApps.find((app) => {
        if (!app) return null;
        const mimeTypes = getMimeTypesForAppVersion(app.currentAppVersion);
        return mimeTypes.some((mimeType) => mimeType === file.type);
      });

      // Application not found for file type, ignore file.
      if (!fileUploadApp) return null;

      const fileUploadProp =
        fileUploadApp.currentAppVersion.presentationProperties.find(
          (p) => p.type === 'file',
        );

      // Ignore file if application doesn't have a file upload prop.
      // This shouldn't happen since getMimeTypesForAppVersion checks for
      // a file upload prop already.
      if (!fileUploadProp) return null;

      // Create a default presentation for the file.
      const presentation = createPresentationFromAppVersion(
        fileUploadApp.currentAppVersion,
        currentUser.id,
        getDefaultThemeForUser(currentUser, themesById),
        domain?.defaultPresentationDuration ?? null,
      );
      presentation.name = file.name;
      presentation.applicationVariables[fileUploadProp.name] = {
        filename: file.name,
        'content-type': file.type,
        'content-length': file.size,
      };

      // Make sure we set the source to pdf for presenter apps.
      // https://github.com/mirainc/powerpoint/blob/staging/src/constant/sources.js#L3
      if (presentation.applicationId === config.presenterAppId) {
        presentation.applicationVariables.source = 'pdf';
      }

      // We currently don't support file uploads that aren't at the
      // root level (ie. file upload inside an array input) but we
      // need to fake the path anyways.
      const path = ['applicationVariables', fileUploadProp.name];
      presentationsToSave.push(
        new Promise((resolve) =>
          dispatch(
            presActions.savePresentation(presentation, {
              fileUploads: [
                { path, file, localUrl: URL.createObjectURL(file) },
              ],
              folderId,
              onSave: (savedPresentation) => resolve(savedPresentation),
            }),
          ),
        ),
      );
    });

    const savedPresentations = await Promise.all(presentationsToSave);
    if (selectionId) {
      savedPresentations.forEach(({ id }) => {
        dispatch(
          addContentPageActions.selectItem({
            selectionId,
            item: { id, type: 'presentation' },
          }),
        );
      });
    }

    history.push(backTo);
  };

  const newPlaylist = () => {
    if (createPlaylistTo) {
      history.push(createPlaylistTo);
    } else {
      history.push(
        paths.newPlaylist({
          playlistId: createNewId(),
          folderId,
          // We don't want to come back to the app selector page,
          // forward the backTo to the next page.
          backTo,
          saveTo: backTo,
          backToLabel: getBackToLabel(),
        }),
      );
    }
  };

  const renderHeaderBar = () => {
    return (
      isModal && (
        <ActionBar>
          Create a presentation, or just drag an image, video or PDF file into
          this window to create one instantly.
          <Spacer />
          <Button label="Back to Library" onClick={goBack} />
        </ActionBar>
      )
    );
  };

  const renderFooterBar = () => {
    return (
      !isModal && (
        <ActionBar>
          <Button label="Cancel" onClick={goBack} />
          <Spacer />
        </ActionBar>
      )
    );
  };

  const renderAvailableApps = () => {
    return (
      <Grid>
        <GridItem key="new-playlist">
          <PlaylistCard playlist={defaultPlaylist} onClick={newPlaylist} />
        </GridItem>
        {applications
          .filter((app) => !app.isDeprecated && !isResourceDeleted(app))
          .map((app) => (
            <GridItem key={app.id}>
              <ApplicationCard
                application={app}
                onClick={() => {
                  if (quickUploadAppIds.includes(app.id)) {
                    openFileBrowser(
                      getMimeTypesForAppVersion(app.currentAppVersion),
                    );
                  } else {
                    newPresentation(app.id);
                  }
                }}
              />
            </GridItem>
          ))}
      </Grid>
    );
  };

  const pageProps = {
    title: 'Create a presentation',
    backTo,
    hideNavigation: isModal,
  };

  if (isLoading && !applications) {
    return (
      <Page {...pageProps}>
        <Paper color="light" className={classes.paper}>
          {renderHeaderBar()}
          <Spacer />
          {renderFooterBar()}
        </Paper>
      </Page>
    );
  }

  return (
    <Page {...pageProps}>
      <Paper color="light" className={classes.paper}>
        <FileDropper
          title="Drag your pictures, videos or PDFs here to quick upload."
          onDrop={createPresentationsForFiles}
        />
        <input
          ref={fileInput}
          multiple
          type="file"
          onChange={handleFileInputChange}
          style={{ position: 'absolute', top: -9999, opacity: 0 }}
        />
        {renderHeaderBar()}
        <Scrollable>
          {renderAvailableApps()}
          <MarketplaceBanner />
        </Scrollable>
        {renderFooterBar()}
      </Paper>
    </Page>
  );
};

export default ApplicationsPage;
